import { ActionIcon } from "@taunggyi-plus/design-system";
import { useFeedback } from "@taunggyi-plus/auth";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@taunggyi-plus/auth";
import { Card } from "@taunggyi-plus/design-system";
import {
  defaults,
  incomeTotals,
  money,
  queueInfo,
  serviceLabels,
  viewLabels,
  yangonDate,
  type DashboardData,
  type DashboardFilters,
  type DashboardView,
} from "./dashboard-model";

const storageKey = "taunggyi-admin-dashboard-filters";
function initialFilters(): DashboardFilters {
  try {
    const saved = JSON.parse(
      sessionStorage.getItem(storageKey) ?? "null",
    ) as DashboardFilters | null;
    if (!saved) return defaults;
    return {
      view: Object.hasOwn(viewLabels, saved.view) ? saved.view : defaults.view,
      service:
        saved.service === "all" || Object.hasOwn(serviceLabels, saved.service)
          ? saved.service
          : "all",
      overdue: saved.overdue === true,
      period: ["today", "month", "all"].includes(saved.period) ? saved.period : defaults.period,
    };
  } catch {
    return defaults;
  }
}

export function DashboardMetrics() {
  const auth = useAuth();
  const dialog = useRef<HTMLDialogElement>(null);
  const request = useRef({ value: 0 });
  const [data, setData] = useState<DashboardData | null>(null);
  const [filters, setFilters] = useState(initialFilters);
  const [draft, setDraft] = useState(filters);
  const [error, setError] = useFeedback("", "error");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    if (!auth.client || !auth.user) return;
    const sequence = ++request.current.value;
    setBusy(true);
    try {
      const [result, followup] = await Promise.all([
        auth.client.rpc("admin_dashboard_overview", {}),
        auth.client.rpc("admin_followup_status"),
      ]);
      if (followup.error) throw new Error(followup.error.message);
      if (result.error) throw new Error(result.error.message);
      if (request.current.value !== sequence) return;
      setData({
        ...(result.data as DashboardData),
        followup: followup.data as DashboardData["followup"],
      });
      setError("");
    } catch {
      if (request.current.value === sequence)
        setError("Dashboard could not be refreshed. Please try again.");
    } finally {
      if (request.current.value === sequence) setBusy(false);
    }
  }, [auth.client, auth.user, setError]);
  useEffect(() => {
    const lifecycle = request.current;
    const initial = window.setTimeout(() => void load(), 0);
    const refresh = () => {
      if (!document.hidden) void load();
    };
    const timer = window.setInterval(refresh, 60_000);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      ++lifecycle.value;
      window.clearTimeout(initial);
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [load]);
  useEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify(filters));
  }, [filters]);
  const services =
    data?.services.filter((s) => filters.service === "all" || filters.service === s.kind) ?? [];
  const available = services.filter((s) => s.available);
  const tasks = available.flatMap((service) =>
    service.tasks
      .filter(
        (t) =>
          t.count > 0 &&
          (filters.view !== "todo" ||
            !filters.overdue ||
            data?.followup?.overdue.some(
              (o) => o.service === service.kind && o.queue === t.queue && o.count > 0,
            )),
      )
      .map((task) => ({ ...task, service })),
  );
  const totals = incomeTotals(available, filters.period, yangonDate());
  const supportCount =
    !filters.overdue && filters.service === "all" ? (data?.support_unread ?? 0) : 0;
  const overdueCount = (service: string, queue: string) =>
    data?.followup?.overdue.find((o) => o.service === service && o.queue === queue)?.count ?? 0;
  const outstanding = tasks.reduce(
    (sum, t) => sum + (filters.overdue ? overdueCount(t.service.kind, t.queue) : t.count),
    supportCount,
  );
  const periodLabel = { all: "All time", month: "This month", today: "Today" }[filters.period];
  const received = (data?.followup?.receipts ?? [])
    .filter(
      (r) =>
        (filters.service === "all" || r.service === filters.service) &&
        r.received_on <= yangonDate() &&
        (filters.period === "all" ||
          r.received_on >=
            (filters.period === "today" ? yangonDate() : `${yangonDate().slice(0, 7)}-01`)),
    )
    .reduce((n, r) => n + Number(r.amount_minor), 0);
  return (
    <section className="admin-service-dashboard" aria-label="Admin dashboard">
      <div className="admin-dashboard-heading">
        <div>
          <h1>Dashboard</h1>
        </div>
        <button
          className="toolbar-icon-button"
          type="button"
          aria-label="Filter dashboard"
          onClick={() => {
            setDraft(filters);
            dialog.current?.showModal();
          }}
        >
          <ActionIcon action="filter" />
        </button>
      </div>
      <div className="admin-dashboard-views" role="group" aria-label="Switch dashboard view">
        {(Object.entries(viewLabels) as [DashboardView, string][]).map(([view, label]) => (
          <button
            type="button"
            key={view}
            aria-pressed={filters.view === view}
            onClick={() => setFilters({ ...filters, view })}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="admin-dashboard-context">
        <p>
          {filters.service === "all" ? "All services" : serviceLabels[filters.service]} ·{" "}
          {filters.view === "income" ? `${periodLabel} · Myanmar time` : "Current totals"}
        </p>
        <button
          type="button"
          className="admin-dashboard-refresh"
          disabled={busy}
          onClick={() => void load()}
        >
          {busy ? "Refreshing…" : "Refresh"}
        </button>
      </div>
      {error && (
        <p role="alert">
          {error}
          {data ? " Showing the last loaded figures." : ""}
        </p>
      )}
      {!data && !error && <p role="status">Loading dashboard…</p>}
      {data && (
        <>
          {filters.view === "current" && (
            <>
              <div className="admin-dashboard-totals">
                <Summary
                  label="Live providers"
                  value={available.reduce((n, s) => n + s.live_providers, 0).toLocaleString()}
                  help="Published / enabled in active organizations"
                />
                <Summary
                  label="Waiting for a response / assignment"
                  value={tasks
                    .filter((t) => ["pending", "dispatch", "unassigned"].includes(t.queue))
                    .reduce((n, t) => n + t.count, 0)
                    .toLocaleString()}
                  help="Bookings, orders and delivery jobs"
                />
                <Summary
                  label="Ongoing service work"
                  value={tasks
                    .filter((t) => t.queue === "active")
                    .reduce((n, t) => n + t.count, 0)
                    .toLocaleString()}
                  help="Current / upcoming stays and active orders"
                />
              </div>
              <div className="admin-service-metrics">
                {services.map((service) => (
                  <Card key={service.kind} className="admin-service-metric">
                    <div className="admin-service-title">
                      <h2>{service.label}</h2>
                      <span
                        className="admin-dashboard-badge"
                        data-live={service.live_providers > 0}
                      >
                        {!service.available
                          ? "Coming soon"
                          : service.live_providers > 0
                            ? "Published"
                            : service.total_providers
                              ? "Not published"
                              : "No providers"}
                      </span>
                    </div>
                    {service.available ? (
                      <>
                        <dl className="admin-status-grid">
                          <Metric
                            label="Live / total providers"
                            value={`${service.live_providers} / ${service.total_providers}`}
                          />
                          <Metric
                            label="Active / total members"
                            value={`${service.active_users} / ${service.total_users}`}
                          />
                          <Metric
                            label={service.capacity_label ?? "Capacity"}
                            value={service.capacity.toLocaleString()}
                          />
                          <Metric
                            label="Work items outstanding"
                            value={service.tasks.reduce((n, t) => n + t.count, 0).toLocaleString()}
                          />
                        </dl>
                        <button
                          className="admin-dashboard-text-action"
                          type="button"
                          onClick={() =>
                            setFilters({ ...filters, view: "todo", service: service.kind })
                          }
                        >
                          View service work →
                        </button>
                      </>
                    ) : (
                      <p className="muted">This service has not launched.</p>
                    )}
                  </Card>
                ))}
              </div>
              <details className="admin-dashboard-notes">
                <summary>What these figures mean</summary>
                <p>
                  Members are active memberships in enabled organizations, excluding removed
                  memberships. Availability is the saved service setting, not a live connection
                  check. Room types are listings, not vacant room counts. A food order and its
                  delivery can create separate work items.
                </p>
              </details>
            </>
          )}
          {filters.view === "income" && (
            <>
              <div className="admin-income-totals">
                <Summary
                  label="Platform income recorded"
                  value={money(totals.platform)}
                  help="Subscription fees + promotion fees"
                />
                <Summary
                  label="Provider income recorded"
                  value={money(totals.provider)}
                  help="Service sales + manual income"
                />
              </div>
              <p className="admin-dashboard-caption">
                Booking values and delivery cash collections are shown separately.
              </p>
              <Summary
                label="Platform payments received"
                value={money(received)}
                help="Explicit receipts in this period, by date received"
              />
              <div className="admin-service-metrics">
                {available.map((service) => {
                  const income = incomeTotals([service], filters.period, yangonDate());
                  return (
                    <Card key={service.kind} className="admin-service-metric">
                      <h2>{service.label}</h2>
                      <dl className="admin-income-breakdown">
                        <Metric label="Subscription fees" value={money(income.subscriptions)} />
                        <Metric label="Promotion fees" value={money(income.promotions)} />
                        <Metric label="Event fees" value={money(income.events)} />
                        <Metric label="Service sales" value={money(income.sales)} />
                        <Metric label="Manual provider income" value={money(income.manual)} />
                        <Metric
                          label="Recorded expenses / refunds"
                          value={money(income.expenses)}
                        />
                        {service.kind === "hotel" && (
                          <Metric label="Confirmed booking value" value={money(income.bookings)} />
                        )}
                        {service.kind === "delivery_fleet" && (
                          <Metric label="Cash collected for orders" value={money(income.cash)} />
                        )}
                      </dl>
                    </Card>
                  );
                })}
              </div>
              {!available.length && (
                <Card className="empty-state">
                  This service has not launched. No income to report.
                </Card>
              )}
              <details className="admin-dashboard-notes">
                <summary>How income is counted</summary>
                <p>
                  These are recorded ledger amounts, not platform profit or verified cash receipts.
                  Platform fees are recorded when subscriptions or promotions are approved. Food
                  sales and taxi fares use service-sale entries; manual income belongs to the
                  provider. Confirmed hotel bookings and delivery cash collections are excluded from
                  income totals to avoid mixing booking commitments and cash collected for orders
                  with sales. Customer expense copies are excluded. Dates use Myanmar time.
                </p>
              </details>
              <a className="admin-dashboard-text-action" href="/finance">
                Open finance records →
              </a>
            </>
          )}
          {filters.view === "todo" && (
            <>
              <div className="admin-work-heading">
                <h2>
                  {outstanding.toLocaleString()} {filters.overdue ? "overdue" : "outstanding"} work
                  items
                </h2>
              </div>
              <div className="admin-work-grid">
                {tasks.map(({ service, ...task }) => {
                  const info = queueInfo(service.kind, task.queue);
                  if (!info) return null;
                  return (
                    <a
                      className="admin-work-card"
                      key={`${service.kind}:${task.queue}`}
                      href={
                        filters.overdue && info.href.includes("/more/operations?")
                          ? `${info.href}&overdue=1`
                          : info.href
                      }
                    >
                      <span className="admin-work-card__count">
                        {(filters.overdue
                          ? overdueCount(service.kind, task.queue)
                          : task.count
                        ).toLocaleString()}
                      </span>
                      <span>
                        <small>
                          {service.label} · {info.owner}
                        </small>
                        <strong>{info.title}</strong>
                        {overdueCount(service.kind, task.queue) > 0 && (
                          <span className="overdue-indicator">
                            {overdueCount(service.kind, task.queue)} overdue
                          </span>
                        )}
                        {task.oldest && (
                          <small>
                            Oldest:{" "}
                            {new Date(task.oldest).toLocaleDateString("en-GB", {
                              timeZone: "Asia/Yangon",
                            })}
                          </small>
                        )}
                      </span>
                      <span aria-hidden="true">→</span>
                    </a>
                  );
                })}
                {supportCount > 0 && (
                  <button
                    className="admin-work-card"
                    type="button"
                    onClick={() => window.dispatchEvent(new Event("taunggyi:open-support"))}
                  >
                    <span className="admin-work-card__count">{supportCount}</span>
                    <span>
                      <small>Customer support · Admin</small>
                      <strong>Unread conversations</strong>
                      <small>Open the support inbox</small>
                    </span>
                    <span aria-hidden="true">→</span>
                  </button>
                )}
              </div>
              {outstanding === 0 && (
                <Card className="empty-state">
                  <strong>No outstanding work in this view.</strong>
                  <p>
                    {available.length
                      ? "You are up to date with the recorded queues."
                      : "This service has not launched."}
                  </p>
                </Card>
              )}
              <a className="admin-dashboard-text-action" href="/more/work-thresholds">
                Manage overdue thresholds →
              </a>
              <p className="admin-dashboard-caption">
                Counts are work items; an order may need work from more than one team. Customer
                support appears under All services.
              </p>
            </>
          )}
          <p className="dashboard-updated">
            Updated{" "}
            {new Date(data.generated_at).toLocaleTimeString("en-GB", { timeZone: "Asia/Yangon" })}{" "}
            Myanmar time · Refreshes every minute
          </p>
        </>
      )}
      <dialog
        ref={dialog}
        className="customer-order-filter-dialog"
        aria-labelledby="admin-dashboard-filter-title"
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setFilters(draft);
            dialog.current?.close();
          }}
        >
          <header>
            <h2 id="admin-dashboard-filter-title">Filter dashboard</h2>
            <button
              type="button"
              className="button button--secondary"
              aria-label="Close filters"
              onClick={() => dialog.current?.close()}
            >
              ×
            </button>
          </header>
          <label>
            Dashboard view
            <select
              value={draft.view}
              onChange={(event) =>
                setDraft({ ...draft, view: event.target.value as DashboardView })
              }
            >
              {Object.entries(viewLabels).map(([view, label]) => (
                <option key={view} value={view}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Service
            <select
              value={draft.service}
              onChange={(event) => setDraft({ ...draft, service: event.target.value })}
            >
              <option value="all">All services</option>
              {Object.entries(serviceLabels).map(([kind, label]) => (
                <option key={kind} value={kind}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          {draft.view === "todo" && (
            <label>
              Work status
              <select
                value={draft.overdue ? "overdue" : "all"}
                onChange={(e) => setDraft({ ...draft, overdue: e.target.value === "overdue" })}
              >
                <option value="all">All outstanding work</option>
                <option value="overdue">Overdue only</option>
              </select>
            </label>
          )}
          {draft.view === "income" && (
            <label>
              Income period
              <select
                value={draft.period}
                onChange={(event) => setDraft({ ...draft, period: event.target.value })}
              >
                <option value="today">Today</option>
                <option value="month">This month</option>
                <option value="all">All time</option>
              </select>
            </label>
          )}
          <footer>
            <button
              type="button"
              className="button button--secondary"
              onClick={() => setDraft(defaults)}
            >
              Reset
            </button>
            <button className="button button--primary" type="submit">
              Apply filters
            </button>
          </footer>
        </form>
      </dialog>
    </section>
  );
}
function Summary({ label, value, help }: { label: string; value: string; help: string }) {
  return (
    <Card className="admin-dashboard-total">
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{help}</small>
    </Card>
  );
}
function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
